import React, { useState, useEffect, useContext } from 'react';
import { observer } from 'mobx-react-lite';
import { ThemeProvider, CssBaseline } from '@mui/material';
import muiTheme from './theme/muiTheme';
import api, { onUnauthorized } from './http';
import { BrowserRouter as Router, Route, Routes, Navigate, Link, useLocation } from 'react-router-dom';
import Scrollable from './components/Scrollable';
import './App.css';
import Header from './components/Header';
import Footer from './components/Footer';
import AuthorizationModal from './components/store/AuthorizationModal';
import { adminRoutes, publicRoutes, userRoutes } from './routes';
import { HomePageRoute } from './consts';
import { Context } from './index';
import { useElementVisibility } from './hooks/useElementVisibility';

// 404 — нормальная пустая страница с CTA
function NotFound() {
  const location = useLocation();
  return (
    <div className="not-found-page">
      <div className="not-found-card">
        <div className="not-found-code">404</div>
        <h1 className="not-found-title">Страница не найдена</h1>
        <p className="not-found-text">
          Похоже, такой страницы нет. Возможно адрес <code>{location.pathname}</code> устарел
          или у текущей роли нет к нему доступа.
        </p>
        <div className="not-found-actions">
          <Link to="/" className="not-found-btn">На главную</Link>
          <button
            type="button"
            className="not-found-btn not-found-btn--ghost"
            onClick={() => window.history.back()}
          >Назад</button>
        </div>
      </div>
    </div>
  );
}

const App = observer(function App() {
  const [isAuthModalOpen, setAuthModalOpen] = useState(false);
  const [roleReady, setRoleReady] = useState(false);
  const { user } = useContext(Context);
  // источник истины — MobX-store, не локальный state
  const role = user.isAuth ? String(user.role) : 'public';

  useEffect(() => {
    // Регидрация при F5: если есть токен — спрашиваем /v1/me;
    // если токена нет — пробуем refresh-cookie через /v1/refresh.
    let cancelled = false;

    const restore = async () => {
      try {
        if (user.accessToken) {
          const { data } = await api.get('v1/me');
          if (cancelled) return;
          user.hydrateFromAuth({ ...data, accessToken: user.accessToken });
        } else {
          try {
            const { data } = await api.post('v1/refresh', {});
            user.setAccessToken(data.accessToken);
            const me = await api.get('v1/me');
            if (cancelled) return;
            user.hydrateFromAuth({ ...me.data, accessToken: data.accessToken });
          } catch {
            // нет валидной сессии — ничего страшного, остаёмся в public
          }
        }
      } catch {
        user.logout();
      } finally {
        if (!cancelled) setRoleReady(true);
      }
    };

    onUnauthorized(() => { user.logout(); });

    restore();
    return () => { cancelled = true; };
  }, [user]);

  const toggleAuthModal = () => setAuthModalOpen(prev => !prev);

  const renderRoutes = (routes) =>
    routes.map(({ path, Component }) => <Route key={path} path={path} element={<Component />} />);

  // Пока роль не определена — ничего не рендерим, чтобы не сработал catch-all
  if (!roleReady) {
    return (
      <Scrollable>
        <div className="app-container">
          {/* можно показать спиннер, если хочешь */}
        </div>
      </Scrollable>
    );
  }

  // Набор маршрутов по роли
  let roleRoutes = null;
  if (role === '1') roleRoutes = renderRoutes(adminRoutes);
  else if (role === '3') roleRoutes = renderRoutes(userRoutes);
  else roleRoutes = renderRoutes(publicRoutes);

  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      <Scrollable>
        <Router /* basename="/если_нужно_подпапка" */>
          <div className="app-container">
            <HeaderWrapper onAuthClick={toggleAuthModal} />
            <Routes>
              {roleRoutes}
              <Route path="*" element={<NotFound />} />
            </Routes>
            <FooterWrapper />
            <AuthorizationModal isOpen={isAuthModalOpen} onClose={toggleAuthModal} />
          </div>
        </Router>
      </Scrollable>
    </ThemeProvider>
  );
});

function HeaderWrapper({ onAuthClick }) {
  const isVisible = useElementVisibility();
  if (!isVisible) return null;
  return (
    <div className="header-container">
      <Header onAuthClick={onAuthClick} />
    </div>
  );
}

function FooterWrapper() {
  const isVisible = useElementVisibility();
  return (
    <div className={`footer-container ${!isVisible ? 'hidden-footer' : ''}`}>
      <Footer />
    </div>
  );
}

export default App;
